import React, { useRef } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import hpclLogo from '../assets/Hindustan_Petroleum-Logo.wine.png';
import bhelLogo from '../assets/Bharat_Heavy_Electricals_Limited-Logo.wine.png';
import ghardaLogo from '../assets/Gharda-Registered.png';
import kimsLogo from '../assets/kims-logo-transparent.png';
import crodaLogo from '../assets/Croda_Green_Logo.jpg';
import npcilLogo from '../assets/npcil.jpg';
import kpdlLogo from '../assets/kpdl .jpg';
import dehuLogo from '../assets/dehu .jpg';
import ntpcLogo from '../assets/Untitled design.jpg';
import relianceLogo from '../assets/reliance ind.jpeg';
import forbesLogo from '../assets/forbes-marshel.png';
import goelGangaLogo from '../assets/goel ganga.jpeg';
import pmrdaLogo from '../assets/pmrda-logo.jpg';
import birlaCarbonLogo from '../assets/birla carbon.jpeg';
import kingfaLogo from '../assets/kinfa .jpg';
import bmcLogo from '../assets/bmc.png';
import pcmcLogo from '../assets/Official_Logo_of_PCMC.jpeg';
import mjpLogo from '../assets/MJP logo.jpg';
import bharatForgeLogo from '../assets/bharat-forge-logo-.png';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const CLIENTS = [
  { name: 'Hindustan Petroleum', src: hpclLogo },
  { name: 'BHEL', src: bhelLogo },
  { name: 'Gharda', src: ghardaLogo },
  { name: 'KIMS Hospitals', src: kimsLogo },
  { name: 'Croda', src: crodaLogo },
  { name: 'NPCIL', src: npcilLogo },
  { name: 'Kolte-Patil', src: kpdlLogo },
  { name: 'DEHU', src: dehuLogo },
  { name: 'NTPC', src: ntpcLogo },
  { name: 'Reliance Industries', src: relianceLogo },
  { name: 'Forbes Marshall', src: forbesLogo },
  { name: 'Goel Ganga Group', src: goelGangaLogo },
  { name: 'PMRDA', src: pmrdaLogo },
  { name: 'Birla Carbon', src: birlaCarbonLogo },
  { name: 'KINGFA', src: kingfaLogo },
  { name: 'BMC', src: bmcLogo },
  { name: 'PCMC', src: pcmcLogo },
  { name: 'MJP', src: mjpLogo },
  { name: 'Bharat Forge', src: bharatForgeLogo },
];

function LogoItem({ client }) {
  return (
    <Box
      className="client-logo-item"
      sx={{
        flexShrink: 0,
        width: { xs: 210, sm: 250, md: 280 },
        height: { xs: 110, sm: 128, md: 144 },
        mx: { xs: 1.5, md: 2.25 },
        px: 3.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        borderRadius: '18px',
        border: '1px solid',
        borderColor: alpha('#0d47a1', 0.08),
        boxShadow: '0 4px 16px rgba(13, 71, 161, 0.06)',
      }}
    >
      <Box
        component="img"
        src={client.src}
        alt={client.name}
        decoding="async"
        draggable={false}
        sx={{
          maxWidth: '90%',
          maxHeight: '72%',
          objectFit: 'contain',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

export default function ClientsShowcase() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const rowRef = useRef(null);
  const trackRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set([headerRef.current, rowRef.current], { autoAlpha: 1, y: 0 });
      });

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const track = trackRef.current;
        const row = rowRef.current;
        if (!track || !row) return;

        gsap.set(headerRef.current, { autoAlpha: 0, y: 24 });
        gsap.set(row, { autoAlpha: 0, y: 28 });

        gsap
          .timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 82%',
              toggleActions: 'play none none reverse',
            },
            defaults: { ease: 'power3.out' },
          })
          .to(headerRef.current, { autoAlpha: 1, y: 0, duration: 0.75 })
          .to(row, { autoAlpha: 1, y: 0, duration: 0.75 }, '-=0.4');

        // Single-element marquee: animate the whole track once.
        // Far smoother than animating every logo individually.
        let tween;
        const PIXELS_PER_SECOND = 55;

        const buildMarquee = () => {
          tween?.kill();
          gsap.set(track, { x: 0, force3D: true });

          // Half width = one full set of logos (content is duplicated once)
          const half = track.scrollWidth / 2;
          if (!half) return;

          tween = gsap.to(track, {
            x: -half,
            duration: half / PIXELS_PER_SECOND,
            ease: 'none',
            repeat: -1,
            force3D: true,
          });
        };

        buildMarquee();

        const visibilityTrigger = ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          onEnter: () => tween?.play(),
          onEnterBack: () => tween?.play(),
          onLeave: () => tween?.pause(),
          onLeaveBack: () => tween?.pause(),
        });

        const onEnter = () => {
          if (tween) gsap.to(tween, { timeScale: 0.2, duration: 0.5, ease: 'power2.out', overwrite: true });
        };
        const onLeave = () => {
          if (tween) gsap.to(tween, { timeScale: 1, duration: 0.6, ease: 'power2.out', overwrite: true });
        };
        row.addEventListener('mouseenter', onEnter);
        row.addEventListener('mouseleave', onLeave);

        let resizeTimer;
        const onResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            buildMarquee();
            ScrollTrigger.refresh();
          }, 250);
        };
        window.addEventListener('resize', onResize);

        // Rebuild once images finish loading so width is accurate
        const images = track.querySelectorAll('img');
        let pending = images.length;
        const maybeRebuild = () => {
          pending -= 1;
          if (pending <= 0) {
            buildMarquee();
            ScrollTrigger.refresh();
          }
        };
        if (pending === 0) {
          buildMarquee();
        } else {
          images.forEach((img) => {
            if (img.complete) maybeRebuild();
            else {
              img.addEventListener('load', maybeRebuild, { once: true });
              img.addEventListener('error', maybeRebuild, { once: true });
            }
          });
        }

        return () => {
          clearTimeout(resizeTimer);
          window.removeEventListener('resize', onResize);
          row.removeEventListener('mouseenter', onEnter);
          row.removeEventListener('mouseleave', onLeave);
          visibilityTrigger.kill();
          tween?.kill();
        };
      });

      return () => mm.revert();
    },
    { scope: sectionRef, dependencies: [isDark], revertOnUpdate: true }
  );

  return (
    <Box
      ref={sectionRef}
      className="clients-showcase"
      sx={{
        mt: { xs: 6, md: 8 },
        position: 'relative',
      }}
    >
      <Box ref={headerRef} sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, px: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: 2,
            display: 'block',
            mb: 1.5,
          }}
        >
          OUR CLIENTS
        </Typography>
        <Typography
          variant="h4"
          component="h3"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.4px',
            mb: 1.5,
            color: isDark ? 'primary.light' : undefined,
            background: isDark
              ? undefined
              : 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
            backgroundClip: isDark ? undefined : 'text',
            WebkitBackgroundClip: isDark ? undefined : 'text',
            WebkitTextFillColor: isDark ? undefined : 'transparent',
          }}
        >
          Trusted by Industry Leaders
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: 480,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Powering India's Water Infrastructure with Reliable Solutions
        </Typography>
      </Box>

      <Box
        ref={rowRef}
        className="clients-marquee-row"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: 2,
          // Soft edge fade without CSS mask (masks can cause paint lag)
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: { xs: 40, md: 80 },
            zIndex: 2,
            pointerEvents: 'none',
          },
          '&::before': {
            left: 0,
            background: isDark
              ? 'linear-gradient(to right, rgba(15,20,36,0.95), rgba(15,20,36,0))'
              : 'linear-gradient(to right, rgba(245,249,255,0.98), rgba(245,249,255,0))',
          },
          '&::after': {
            right: 0,
            background: isDark
              ? 'linear-gradient(to left, rgba(15,20,36,0.95), rgba(15,20,36,0))'
              : 'linear-gradient(to left, rgba(245,249,255,0.98), rgba(245,249,255,0))',
          },
        }}
      >
        <Box
          ref={trackRef}
          sx={{
            display: 'flex',
            width: 'max-content',
            alignItems: 'center',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
          }}
        >
          {/* Duplicate once — seamless loop by translating half the track */}
          {[...CLIENTS, ...CLIENTS].map((client, index) => (
            <LogoItem key={`${client.name}-${index}`} client={client} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
